import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase, supabaseEnabled } from "./supabase";
import { clearAllReadThroughCache } from "./cache";

interface AuthContextValue {
  session: Session | null;
  loading: boolean;
  /** True after the user lands from a password-recovery email — UI should
      show a "set new password" screen instead of the normal app, regardless
      of whether `session` is also populated. */
  isPasswordRecovery: boolean;
  /** Call after the user successfully sets a new password — clears the
      recovery flag so they can use the app normally. */
  clearPasswordRecovery: () => void;
  /** If the user landed back here from an email link that Supabase rejected
      (expired token, already-consumed magic link, malformed link, etc.), this
      holds a human-readable message we should surface on the sign-in screen.
      null when there's no auth-link error to display. */
  authLinkError: string | null;
  /** Clear the auth-link error — called once the user has acknowledged it
      (e.g. by submitting a new sign-in / requesting a new link). */
  clearAuthLinkError: () => void;
  // Password
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUpWithPassword: (email: string, password: string) => Promise<void>;
  setPassword: (password: string) => Promise<void>;
  /** Sends a password-reset email with a link that, when clicked, drops the
      user back into the app in recovery mode (PASSWORD_RECOVERY event). */
  sendPasswordResetEmail: (email: string) => Promise<void>;
  // OTP / magic link (fallback for existing users)
  signInWithMagicLink: (email: string) => Promise<void>;
  verifyEmailOtp: (email: string, token: string) => Promise<void>;
  signOut: () => Promise<void>;
  enabled: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(supabaseEnabled);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [authLinkError, setAuthLinkError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    /**
     * Force a clean session swap when landing from a magic-link / recovery
     * email. Supabase's default `detectSessionInUrl` reads the URL fragment
     * and calls setSession, BUT if there's already a cached session in
     * localStorage for a different user, the swap can be inconsistent —
     * we've seen users click a link for account A and end up signed in as
     * account B (the cached session).
     *
     * Belt-and-suspenders fix: when we see access_token + type=magiclink |
     * recovery | signup | invite in the hash, we:
     *   1. Parse the tokens out of the URL
     *   2. Sign out any existing session (clears localStorage)
     *   3. Install the new session via setSession() — guaranteed to be the
     *      one from the email link
     *   4. Clean the URL so the tokens don't linger in history
     */
    async function handleAuthLinkInUrl(): Promise<boolean> {
      if (typeof window === "undefined") return false;
      const hash = window.location.hash || "";
      const search = window.location.search || "";
      // Log what we see in the URL so a stuck user can DevTools → Console and
      // know exactly why the link did/didn't sign them in. Stripped in
      // production builds? No — left intentionally; these lines are small
      // and the diagnostic value during launch outweighs the bytes.
      console.log("[auth] handleAuthLinkInUrl: hash length =", hash.length, "search length =", search.length);
      if (hash.length > 1) console.log("[auth] hash params:", Object.fromEntries(new URLSearchParams(hash.slice(1)).entries()));
      if (search.length > 1) console.log("[auth] search params:", Object.fromEntries(new URLSearchParams(search.slice(1)).entries()));

      // Supabase sometimes redirects back with an error in the hash (or query)
      // instead of tokens — expired magic links, already-consumed OTPs,
      // malformed links, PKCE mismatches. Before, we silently fell through to
      // the sign-in screen with no feedback; the user blamed themselves.
      const hasErrorInHash = hash.includes("error=") || hash.includes("error_code=");
      const hasErrorInQuery = search.includes("error=") || search.includes("error_code=");
      if (hasErrorInHash || hasErrorInQuery) {
        const source = hasErrorInHash ? hash.slice(1) : search.slice(1);
        const errParams = new URLSearchParams(source);
        const code = errParams.get("error_code");
        const desc = errParams.get("error_description")?.replace(/\+/g, " ");
        let msg: string;
        if (code === "otp_expired") {
          msg = "Your sign-in link expired. Request a new one and click it within a few minutes.";
        } else if (code === "access_denied" && (desc?.toLowerCase().includes("invalid") || desc?.toLowerCase().includes("expired"))) {
          msg = "That sign-in link is no longer valid (already used, or expired). Request a new one.";
        } else {
          msg = desc ? `Sign-in failed: ${desc}` : "Sign-in failed. Try requesting a new link.";
        }
        setAuthLinkError(msg);
        try {
          history.replaceState({}, "", window.location.pathname);
        } catch {
          /* ignore */
        }
        return false;
      }

      // PKCE flow path: Supabase redirects with `?code=...&type=...`. Our
      // client config is implicit, but if Supabase or the email link was
      // generated under PKCE expectations (e.g. a stale link in someone's
      // inbox, or a server-side project setting we don't control), the URL
      // arrives with a code instead of a hash. Try to exchange it.
      const codeParam = new URLSearchParams(search).get("code");
      if (codeParam) {
        try {
          await supabase!.auth.signOut({ scope: "local" });
          clearAllReadThroughCache();
        } catch {
          /* best effort */
        }
        const { error } = await supabase!.auth.exchangeCodeForSession(codeParam);
        if (error) {
          // Most common cause: PKCE code_verifier mismatch (link requested
          // in one browser/session, clicked in another). Surface a useful
          // explanation rather than silent failure.
          console.error("exchangeCodeForSession failed:", error);
          setAuthLinkError(
            `Sign-in failed: ${error.message}. ` +
            `Try requesting a fresh link and clicking it in the same browser/window where you requested it.`
          );
          try {
            history.replaceState({}, "", window.location.pathname);
          } catch {
            /* ignore */
          }
          return false;
        }
        try {
          history.replaceState({}, "", window.location.pathname);
        } catch {
          /* ignore */
        }
        return true;
      }

      if (!hash.includes("access_token=")) return false;

      // Parse the URL fragment (everything after #) as URL search params.
      const params = new URLSearchParams(hash.slice(1));
      const access_token = params.get("access_token");
      const refresh_token = params.get("refresh_token");
      const type = params.get("type"); // magiclink | recovery | signup | invite

      if (!access_token || !refresh_token) return false;

      try {
        // Wipe any cached session for the wrong account before installing
        // the one the user actually clicked through to. Without this, the
        // old session can win the race and Supabase doesn't realize it.
        await supabase!.auth.signOut({ scope: "local" });
        // Also drop the read-through cache — it's keyed by the previous
        // user_id, and we don't want the new user to flash the wrong meals.
        clearAllReadThroughCache();
      } catch {
        // Best effort — even if signOut fails, setSession below will win.
      }

      console.log("[auth] installing session from implicit-flow tokens (type=" + (type ?? "?") + ")");
      const { data, error } = await supabase!.auth.setSession({ access_token, refresh_token });
      if (error) {
        console.error("[auth] setSession from email link failed:", error);
        setAuthLinkError(`Sign-in failed: ${error.message}. Try requesting a fresh link.`);
        return false;
      }
      console.log("[auth] session installed for user:", data.session?.user.email ?? "?");

      // Clean the URL — don't leave tokens in the address bar / history.
      try {
        history.replaceState({}, "", window.location.pathname + window.location.search);
      } catch {
        /* ignore */
      }

      // If the link was a password recovery, flag it so the UI shows the
      // "set new password" screen instead of dropping the user into the app.
      if (type === "recovery") {
        setIsPasswordRecovery(true);
      }
      return true;
    }

    (async () => {
      const swapped = await handleAuthLinkInUrl();
      // If we swapped, the session is already set via setSession (which fires
      // onAuthStateChange below). Either way, do an initial getSession so
      // setLoading flips off cleanly.
      const { data } = await supabase!.auth.getSession();
      setSession(data.session);
      setLoading(false);
      // Avoid unused-variable lint — `swapped` is informational.
      void swapped;
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      // PASSWORD_RECOVERY fires when the user clicks the link in their reset
      // email and lands back here. Supabase auto-creates a recovery session,
      // but we should NOT show the normal app — they need to set a new
      // password first. (handleAuthLinkInUrl above also sets this flag for
      // the case where the URL had type=recovery.)
      if (event === "PASSWORD_RECOVERY") {
        setIsPasswordRecovery(true);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const value: AuthContextValue = {
    session,
    loading,
    isPasswordRecovery,
    clearPasswordRecovery: () => setIsPasswordRecovery(false),
    authLinkError,
    clearAuthLinkError: () => setAuthLinkError(null),
    enabled: supabaseEnabled,
    signInWithPassword: async (email, password) => {
      if (!supabase) throw new Error("Supabase not configured");
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password
      });
      if (error) {
        // Normalize so we don't leak whether the email exists vs whether the
        // password is wrong. Supabase already returns the same string for both
        // these cases, but be explicit so future changes don't regress this.
        throw new Error("Email or password is incorrect.");
      }
    },
    signUpWithPassword: async (email, password) => {
      if (!supabase) throw new Error("Supabase not configured");
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password
      });
      if (error) {
        // Don't leak whether the address is already taken. Supabase's
        // "User already registered" message is the canonical enumeration vector.
        const msg = (error.message || "").toLowerCase();
        if (msg.includes("registered") || msg.includes("exists")) {
          // Same generic outcome as a successful sign-up that pends confirmation.
          throw new Error(
            "If that email isn't already in use, check your inbox to confirm. Otherwise sign in instead."
          );
        }
        throw error;
      }
      if (!data.session) {
        // Email confirmation is required by Supabase. Generic copy so it reads
        // the same whether the account is brand new or already existed.
        throw new Error(
          "Check your inbox for a confirmation link. If you don't see it, the address may already have an account — try signing in."
        );
      }
    },
    setPassword: async (password) => {
      if (!supabase) throw new Error("Supabase not configured");
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
    },
    signInWithMagicLink: async (email: string) => {
      if (!supabase) throw new Error("Supabase not configured");
      const redirectTo = window.location.origin;
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: { emailRedirectTo: redirectTo }
      });
      if (error) throw error;
    },
    sendPasswordResetEmail: async (email: string) => {
      if (!supabase) throw new Error("Supabase not configured");
      // The redirect target should be the app's origin — Supabase appends
      // the recovery token to the URL fragment, the client picks it up on
      // page load and fires PASSWORD_RECOVERY via onAuthStateChange.
      const redirectTo = window.location.origin;
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo
      });
      if (error) {
        // Don't leak whether the email exists. Supabase silently returns
        // success for non-existent emails (sensible default) but be defensive.
        throw error;
      }
    },
    verifyEmailOtp: async (email: string, token: string) => {
      if (!supabase) throw new Error("Supabase not configured");
      const { error } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token: token.trim(),
        type: "email"
      });
      if (error) throw error;
    },
    signOut: async () => {
      // Drop the read-through cache first so a shared device can't show the
      // outgoing user's meals to whoever signs in next.
      clearAllReadThroughCache();
      if (!supabase) return;
      await supabase.auth.signOut();
    }
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
